import React, { useEffect, useContext, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styles from './TasksPage.module.css';
import { TaskHeader } from './components/TaskHeader/TaskHeader';
import { TaskItem } from './components/TaskItem/TaskItem';
import useApplicationSettings from '../../Store/ApplicationSettings';
import { QuestContext, QuestProvider } from './provider/QuestProvider';
import { TaskModal } from '../../Components/TaskModal/TaskModal';
import { useModalStore } from '../../Store/ModalStore';
// import { claimTaskGroup } from '../../Services/Requests';
import { useMainButton } from '../../Components/MainButton/MainButton';
import { Loader } from '../../Components/Loader/Loader';
import { QuestsService } from './services/QuestsService';

// Определим интерфейс для объекта награды
interface QuestReward {
  type: string;
  amount: number;
}

const mapRewardType = (type: string): 'dc' | 'ticket' => {
  return type.toLowerCase() === 'dc' ? 'dc' : 'ticket';
};

const QuestPageContent: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const location = useLocation();
  const groupId = location.state?.groupId;
  const questInfoFromState = location.state?.questInfo;
  const { tasks, questInfo, fetchQuest, loading } = useContext(QuestContext);

  const navigate = useNavigate();
  const tg = window.Telegram.WebApp;
  const { setShowMenubar } = useApplicationSettings((state: any) => ({
    setShowMenubar: state.setShowMenubar
  }));
  const setCurrentTask = useModalStore((state) => state.setCurrentTask);
  const modalOpenRef = useRef(false);
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  const [hasError, setHasError] = useState(false);

  const allTasksCompleted = tasks.length > 0 && tasks.every(task => task.status === 'DONE' || task.status === 'CLAIMED');
  
  const hasCompletedTasks = tasks.some(task => task.status === 'DONE');
  
  const allRewardsClaimed = tasks.length > 0 && tasks.every(task => task.status === 'CLAIMED');

  const [MainButtonComponent, mainButton] = useMainButton({
    text: 'Получить награду',
    color: '#83B63A',
    textColor: '#ffffff',
    isActive: true,
    isVisible: true,
    height: 48,
  });

  useEffect(() => {
    const updateButtonState = () => {
      if (isClaimingReward) {
        mainButton.disable();
        return;
      }
      
      if (allTasksCompleted && hasCompletedTasks) {
        // Можно получить награду
        mainButton.setParams({
          text: 'Собрать награду',
          color: '#83B63A',
          text_color: '#ffffff',
          is_active: true,
          is_visible: true
        });
      } else if (allRewardsClaimed) {
        // Все награды уже получены
        mainButton.setParams({
          text: 'Награда была получена',
          color: 'rgba(255, 255, 255, 0.1)',
          text_color: '#91829C',
          is_active: false,
          is_visible: true
        });
        mainButton.disable();
      } else {
        // Не все задания выполнены
        mainButton.setParams({
          text: 'Сначала выполни задания',
          color: 'rgba(255, 255, 255, 0.1)',
          text_color: '#91829C',
          is_active: false,
          is_visible: true
        });
      }
    };
    
    updateButtonState();
  }, [allTasksCompleted, hasCompletedTasks, allRewardsClaimed, isClaimingReward, hasError]);

  useEffect(() => {
    const setup = () => {
      setShowMenubar(false);
      tg.BackButton.show();
      tg.BackButton.onClick(() => navigate(-1));
    };

    setup();
    
    if (groupId) {
      fetchQuest(groupId);
    }

    return () => {
      tg.BackButton.hide();
      tg.MainButton.hide();
      setShowMenubar(true);
    };
  }, [groupId, navigate, fetchQuest, setShowMenubar]);

  useEffect(() => {
    const handleMainButtonClick = () => {
      if (allTasksCompleted && hasCompletedTasks && !isClaimingReward && groupId) {
        setIsClaimingReward(true);
        setHasError(false);
        mainButton.disable();
        mainButton.showProgress(true);
        
        QuestsService.claimQuestReward(
          groupId, 
          (response) => {
            console.log('Награда за квест получена:', response);
            
            fetchQuest(groupId);
            
            setIsClaimingReward(false);
            mainButton.hideProgress();
          },
          (error) => {
            console.error('Ошибка при получении награды:', error);
            
            setHasError(true);
            mainButton.hideProgress();
            
            setTimeout(() => {
              setIsClaimingReward(false);
            }, 2000);
          }
        );
      }
    };

    mainButton.onClick(handleMainButtonClick);

    return () => {
      mainButton.offClick();
    };
  }, [allTasksCompleted, hasCompletedTasks, isClaimingReward, groupId, fetchQuest]);

  const handleTaskAction = useCallback((taskId: number) => {
    if (modalOpenRef.current) return;
    
    console.log('🎯 Opening modal for quest taskId:', taskId);
    modalOpenRef.current = true;
    
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setCurrentTask(task);
    }
  }, [tasks, setCurrentTask]);

  let headerProps = {
    title: questInfo?.name || '',
    description: questInfo?.description || '',
    image: questInfo?.banner_url || '',
    centerIcon: '/quest_icon.png',
    rewards: [],
  };
  console.log('🎯 questInfo', questInfo);
  const currentQuestInfo = questInfo || questInfoFromState;
  
  if (currentQuestInfo) {
    const mappedRewards = currentQuestInfo.rewards?.map((reward: QuestReward) => ({
      type: mapRewardType(reward.type),
      amount: reward.amount
    }));
    console.log('🎯 currentQuestInfo', currentQuestInfo);
    headerProps = {
      ...headerProps,
      title: currentQuestInfo.name,
      description: currentQuestInfo.description,
      rewards: mappedRewards,
      centerIcon: currentQuestInfo.icon_url || '/quest_icon.png',
    };
  }

  const tasksWithGroup = tasks.map(task => ({
    ...task,
    group: 'combined' as const
  }));

  return (
    <div className={styles.tasksPage}>
      <TaskHeader 
        {...headerProps} 
        isQuest={true}
        groupType={type}
      />
      <div className={styles.tasksListCombined}>
        {loading ? (
          <Loader size="medium" text="Загружаем задания..." />
        ) : tasksWithGroup.length > 0 ? (
          tasksWithGroup.map(task => (
            <TaskItem 
              key={task.id} 
              task={task}
              onTaskAction={handleTaskAction}
            />
          ))
        ) : (
          <div className={styles.tasksListEmpty}>
            Сейчас заданий нет, но скоро они появятся. Заглядывай позже.
          </div>
        )}
      </div>
      <TaskModal />
      <MainButtonComponent />
    </div>
  );
};

const QuestPage: React.FC = () => {
  return (
    <QuestProvider>
      <QuestPageContent />
    </QuestProvider>
  );
};

export default QuestPage; 